type ChannelType = 'marketplace' | 'website';

type UnmappedAttribute = 'category' | 'epoch' | 'style' | 'material' | 'condition';

interface IntegrationMetadata {
	key: string;
	channel: ChannelType;
	title: string;
	description: string;
	details: string;
	logoURL: string;
	guide: string;
	publishURL: string;
	listingURLTemplate: string;
	dailyLimit: number;
}

type ImageFormat = 'jpg' | 'png' | 'webp';

type MainImageVariant = 'cutout' | 'original-squared' | 'original-natural';

interface MarketplaceSettings {
	sendOriginalImages?: boolean;
}

interface WebsiteSettings {
	imageFormat?: ImageFormat;
	mainImageVariant?: MainImageVariant;
}

interface WoocommerceSettings {
	websiteURL?: string;
	consumerKey?: string;
	consumerSecret?: string;
}

type IntegrationSettings = MarketplaceSettings & WebsiteSettings & WoocommerceSettings;

interface MerchantIntegration {
	id: number;
	integrationKey: string;
	enabled: boolean;
	settings: IntegrationSettings;
	createdAt: string;
	metadata?: IntegrationMetadata;
}

interface PublicationLimit {
	limit: number;
	used: number;
}

interface WebsiteAttribute {
	name: string;
	values: Record<number, string>;
}

interface WebsiteTaxonomy {
	categories: Record<number, string>;
	attributes: Record<number, WebsiteAttribute>;
}

interface TextsToMap {
	title: string;
	description: string;
}

interface DimensionsToMap {
	width: number | null;
	length: number | null;
	depth: number | null;
	height: number | null;
	diameter: number | null;
}

interface ImageToMap {
	position: number | null; // null = main image
	url: string;
}

interface MappedProduct {
	texts: Record<string, TextsToMap>;
	price: number;
	currency: string;
	dimensions: DimensionsToMap;
	images: ImageToMap[];
	extra?: Record<string, unknown>;
	unmapped?: UnmappedAttribute[];
}

// buildListingURL builds the URL of the listing of a publication on
// the destination platform, or null when the destination has no such
// link.
const buildListingURL = (mi: MerchantIntegration, externalID: string): string | null => {
	const template = mi.metadata?.listingURLTemplate;
	if (template == null || template === '') {
		return null;
	}
	let url = template.replace('{externalID}', externalID);
	if (url.includes('{websiteURL}')) {
		const websiteURL = mi.settings.websiteURL;
		if (websiteURL == null || websiteURL === '') {
			return null;
		}
		url = url.replace('{websiteURL}', websiteURL.replace(/\/+$/, ''));
	}
	return url;
};

export type {
	ChannelType,
	ImageFormat,
	MainImageVariant,
	IntegrationMetadata,
	MarketplaceSettings,
	WebsiteSettings,
	WoocommerceSettings,
	IntegrationSettings,
	PublicationLimit,
	MerchantIntegration,
	TextsToMap,
	DimensionsToMap,
	ImageToMap,
	MappedProduct,
	UnmappedAttribute,
	WebsiteAttribute,
	WebsiteTaxonomy,
};

export { buildListingURL };
